// ========== src/components/Auth/Register.jsx ==========

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'student',
    parent_email: '',
    website: '' // Honeypot field - should remain empty
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: '', color: '' });
  const [termsAccepted, setTermsAccepted] = useState(false); // FIX: terms checkbox state
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));

    // Check password strength when password field changes
    if (name === 'password') {
      checkPasswordStrength(value);
    }
  };

  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength({ score: 0, message: '', color: '' });
      return;
    }

    let score = 0;
    let message = '';
    let color = '';

    // Length check
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;

    // Contains number or special character
    if (/[0-9!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

    // Contains both uppercase and lowercase
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;

    // Determine strength level
    if (score <= 1) {
      message = 'Silpnas';
      color = 'text-red-600';
    } else if (score === 2) {
      message = 'Vidutinis';
      color = 'text-yellow-600';
    } else if (score === 3) {
      message = 'Geras';
      color = 'text-blue-600';
    } else {
      message = 'Stiprus';
      color = 'text-green-600';
    }

    setPasswordStrength({ score, message, color });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // FIX: validate terms accepted before submitting
    if (!termsAccepted) {
      setError('Privalote sutikti su taisyklėmis ir sąlygomis.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Get reCAPTCHA token
      let recaptchaToken = null;
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('register');
      }

      // Sukuriame duomenų objektą siuntimui, pašalindami tuščią parent_email
      // FIX: include terms_accepted in submission data
      const submissionData = {
        ...formData,
        recaptcha_token: recaptchaToken,
        agreed_to_terms: true  // FIX: must match backend field name
      };
      if (submissionData.role === 'parent' || !submissionData.parent_email) {
        delete submissionData.parent_email;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submissionData) // Siunčiame apdorotus duomenis
        });

        const data = await response.json();

        if (response.ok) {
          setSuccess(true);
          setError('');
          // Don't navigate immediately - show success message
        } else {
          // Išplėstas klaidų apdorojimas
          if (data.detail) {
            // Tikriname, ar 'detail' yra masyvas (Pydantic validacijos klaida)
            if (Array.isArray(data.detail)) {
              // Paimame pirmą klaidos pranešimą iš masyvo
              setError(data.detail[0].msg);
            } else {
              // Jei 'detail' yra paprastas tekstas (pvz., "Email already registered")
              setError(data.detail);
            }
          } else {
            setError('Registracija nepavyko. Įvyko nežinoma klaida.');
          }
        }
      } catch (err) {
        console.error("Registracijos klaida:", err);
        setError('Nepavyko prisijungti prie serverio. Patikrinkite savo interneto ryšį ir bandykite dar kartą.');
      } finally {
        setLoading(false);
      }
    } catch (err) {
      console.error("reCAPTCHA klaida:", err);
      setError('Nepavyko užkrauti apsaugos sistemos. Bandykite dar kartą.');
      setLoading(false);
    }
  };

  // Show success message
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Registracija sėkminga!</h1>
            <p className="text-gray-600 mb-6">
              Patvirtinimo laiškas išsiųstas į <strong>{formData.email}</strong>
            </p>
            <p className="text-gray-600 mb-6">
              Prašome patikrinti savo el. paštą ir paspausti patvirtinimo nuorodą, kad aktyvuotumėte savo paskyrą.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition transform hover:scale-105"
            >
              Eiti į prisijungimą
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">✨</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Registracija</h1>
          <p className="text-gray-600">Sukurkite naują paskyrą</p>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="name">Vardas</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="email">El. paštas</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="password">Slaptažodis</label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-purple-600 transition"
              >
                {showPassword ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {formData.password && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">Slaptažodžio stiprumas:</span>
                  <span className={`text-sm font-bold ${passwordStrength.color}`}>{passwordStrength.message}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.score <= 1 ? 'bg-red-500' :
                      passwordStrength.score === 2 ? 'bg-yellow-500' :
                        passwordStrength.score === 3 ? 'bg-blue-500' : 'bg-green-500'
                      }`}
                    style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Reikalavimai: mažiausiai 8 simboliai, bent vienas skaičius arba specialus simbolis
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="role">Rolė</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
            >
              <option value="student">Mokinys</option>
              <option value="parent">Tėtis/Mama</option>
            </select>
          </div>

          {formData.role === 'student' && (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="parent_email">
                Tėvo el. paštas (neprivaloma)
              </label>
              <input
                id="parent_email"
                name="parent_email"
                type="email"
                value={formData.parent_email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
              />
            </div>
          )}

          {/* Honeypot field - hidden from users, bots will fill it */}
          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            style={{ display: 'none' }}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        {/* FIX: Terms checkbox - required before submission */}
        <div className="flex items-start gap-3 mb-4">
          <input
            id="terms"
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              if (e.target.checked) setError('');
            }}
            className="mt-1 w-4 h-4 accent-purple-600 cursor-pointer flex-shrink-0"
          />
          <label htmlFor="terms" className="text-sm text-gray-500 cursor-pointer">
            Registruodamiesi sutinkate su mūsų svetainės{" "}
            <a
              href="https://www.smartpanda.cloud/taisykles"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-indigo-600 hover:underline"
            >
              Taisyklėmis
            </a>{" "}
            ir{" "}
            <a
              href="https://www.smartpanda.cloud/privatumas"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-indigo-600 hover:underline"
            >
              Privatumo politika
            </a>
          </label>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="flex-1">{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition transform hover:scale-105 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading && (
            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {loading ? 'Registruojamasi...' : 'Registruotis'}
        </button>

        <p className="text-center mt-6 text-gray-600">
          Jau turite paskyrą?{' '}
          <a href="/login" className="text-purple-600 font-bold hover:underline">
            Prisijungti
          </a>
        </p>
      </form>
    </div>
  );
};

// Wrap with reCAPTCHA provider
const Register = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  if (!recaptchaSiteKey) {
    console.warn('reCAPTCHA site key not configured');
    return <RegisterForm />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <RegisterForm />
    </GoogleReCaptchaProvider>
  );
};

export default Register;
